import moment from 'moment';

export function getCurrentWeekInfo(date) {
    // moment.locale("en");
    const currentDate = moment(date) || moment();

    function getWeekDates(currentDate) {
        const startOfWeek = currentDate.startOf('week');
        const weekDates = [];

        for (let i = 0; i < 7; i++) {
            weekDates.push(startOfWeek.clone().add(i, 'day'));
        }

        return weekDates;
    }

    const weekDates = getWeekDates(currentDate);

    return {
        weekNumber: moment(date).isoWeek(),
        weekDates,
        weekArray: [],
    };
}

export function shiftWeek(date, format, shift) {
    const currentWeek = getCurrentWeekInfo(date);
    const weekIndex = currentWeek.weekNumber;

    const shiftByWeeks = (weeks) => {
        let newDate = moment(date).add(weeks, 'week');
        return [newDate, getCurrentWeekInfo(newDate)];
    };

    let weekShift = 1;
    if (format === 'evenweek' || format === 'oddweek') {
        const isEvenWeek = weekIndex % 2 === 0;
        weekShift = (format === 'evenweek' && isEvenWeek) || (format === 'oddweek' && !isEvenWeek) ? 2 : 1;
    }

    return shift === 'previous' ? shiftByWeeks(-weekShift) : shift === 'next' ? shiftByWeeks(weekShift) : null;
}

// Shared iterator for schedule items. Walks `items` and returns the first one
// whose start/end range, repeat pattern and weekday match `currentDate`.
// - Returns { type: 'off',   item } when an off_days entry is hit.
// - Returns { type: 'match', item } when a regular slot matches (item is
//   already overridden by additional_days if applicable).
// - Returns null when nothing matches.
function findScheduleMatch(items, currentDate, currentWeek) {
    const currentFormattedDate = currentDate.format('YYYY-MM-DD');

    for (let i = 0; i < items.length; i++) {
        let item = items[i];
        const startdate = moment(item.start_date, 'YYYY-MM-DD');
        const enddate = item.end_date ? moment(item.end_date, 'YYYY-MM-DD') : null;
        const repeat = item.repeat ?? 'DO_NOT_REPEAT';
        const off_days = item.off_days;
        const additional_days = item.additional_days;

        if (moment(currentDate).isBefore(startdate, 'day')) continue;
        if (enddate && moment(currentDate).isAfter(enddate, 'day')) continue;

        // Verify the rule applies to currentDate based on its repeat pattern
        // BEFORE consulting off_days / additional_days. Exceptions are only
        // meaningful for dates the underlying rule covers.
        switch (repeat) {
            case 'DO_NOT_REPEAT':
                if (currentDate.format('DD-MM-YYYY') !== startdate.format('DD-MM-YYYY')) continue;
                break;
            case 'EVEN':
                if (currentWeek % 2 !== 0) continue;
                break;
            case 'ODD':
                if (currentWeek % 2 === 0) continue;
                break;
            case 'MONTH':
                if (startdate.format('DD') !== currentDate.format('DD')) continue;
                break;
            default:
                break;
        }

        // isoWeekday is locale-safe; format('ddd') breaks when currentDate and startdate
        // were created under different moment locales (e.g. "Mon" vs "man").
        if (repeat !== 'MONTH' && currentDate.isoWeekday() !== startdate.isoWeekday()) continue;

        // Rule covers currentDate — now resolve exceptions.
        if (off_days && off_days.includes(currentFormattedDate)) {
            return { type: 'off', item };
        }

        if (additional_days && Object.keys(additional_days).includes(currentFormattedDate)) {
            item = {
                ...item,
                start_time: additional_days[currentFormattedDate].start_time,
                end_time: additional_days[currentFormattedDate].end_time,
                start_break_time: additional_days[currentFormattedDate].start_break_time,
                end_break_time: additional_days[currentFormattedDate].end_break_time,
            };
        }

        return { type: 'match', item };
    }

    return null;
}

export function eventChecker(emp, controller, index) {
    moment.locale('en');

    if (!emp?.detail || emp.detail.length === 0) return false;

    const currentDate = controller.weekDates[index];
    const currentWeek = controller.weekNumber;

    const result = findScheduleMatch(emp.detail, currentDate, currentWeek);
    if (!result) return null;
    if (result.type === 'off') return { ...result.item, status: 'off' };
    return result.item;
}

export function eventCheckerCalendar(emp, date) {
    const locale = localStorage.getItem('language');
    moment.locale(locale === 'en' ? 'en-gb' : 'da');

    if (!emp?.detail || emp.detail.length === 0) return false;

    const result = findScheduleMatch(emp.detail, date, date?.week());
    if (!result) return null;
    if (result.type === 'off') return { ...result.item, status: 'off' };
    return result.item;
}

export function isEmpAvailable(emp, currentDate) {
    moment.locale('en-gb');

    if (!emp?.availabilityDay) return false;

    const result = findScheduleMatch(emp.availabilityDay, moment(currentDate), moment(currentDate).isoWeek());
    return result?.type === 'match';
}

const compareTime = (item, currentDate) => {
    const currentDateTime = moment(moment(currentDate).format('HH:mm:ss'), 'HH:mm:ss');
    const startTime = moment(item.start_time, 'HH:mm:ss');
    const endTime = moment(item.end_time, 'HH:mm:ss');
    const breakStartTime = moment(item.start_break_time, 'HH:mm:ss');
    const breakEndTime = moment(item.end_break_time, 'HH:mm:ss');

    // Check if current time is between startTime and endTime
    // const isBetweenStartAndEnd = currentDateTime.isBetween(startTime, endTime);
    const isBetweenStartAndEnd = currentDateTime.isSameOrAfter(startTime) && currentDateTime.isBefore(endTime);

    // Check if current time is between breakStartTime and breakEndTime
    // const isBetweenBreak = currentDateTime.isBetween(breakStartTime, breakEndTime);
    const isBetweenBreak = currentDateTime.isSameOrAfter(breakStartTime) && currentDateTime.isBefore(breakEndTime);

    // Final check
    if (isBetweenStartAndEnd && !isBetweenBreak) {
        return true;
    }

    return false;
};

export function isEmpAvailableOnSpecificSlot(emp, currentDate) {
    const weekInfo = getCurrentWeekInfo(currentDate);
    const currentWeek = weekInfo.weekNumber;

    const result = findScheduleMatch(emp.detail, currentDate, currentWeek);
    if (!result || result.type === 'off') return false;
    return compareTime(result.item, currentDate);
}

export function eventFinder({ emp, date }) {
    if (!emp?.detail || emp.detail.length === 0) return null;

    for (let i = 0; i < emp.detail.length; i++) {
        let item = emp.detail[i];

        if (moment(item.start_date).format('ddd') === moment(date).format('ddd')) {
            return item;
        }
    }

    return null;
}

export function getStartEndTime({ emp, date, type = 'WEEK' }) {
    if (!emp?.detail || emp.detail.length === 0) return false;

    for (let i = 0; i < emp.detail.length; i++) {
        let item = emp.detail[i];
        type = item.repeat;

        if (
            type === 'WEEK' ||
            type === 'EVEN' ||
            type === 'ODD' ||
            (type === null && moment(item.start_date).format('ddd') === moment(date).format('ddd'))
        ) {
            const startTime = moment(item.start_time, 'HH:mm:ss').format('HH:mm');
            const endTime = moment(item.end_time, 'HH:mm:ss').format('HH:mm');
            return { startTime, endTime, item };
        }

        if (type === 'MONTH' || (type === null && moment(item.start_date).format('DD') === moment(date).format('DD'))) {
            const startTime = moment(item.start_time, 'HH:mm:ss').format('HH:mm');
            const endTime = moment(item.end_time, 'HH:mm:ss').format('HH:mm');
            return { startTime, endTime, item };
        }
    }

    return false;
}
